import React, { useEffect, useReducer, createContext, Dispatch } from 'react';
import { AuthAction, AuthState, AuthUser } from '../../interfaces/Contexts';
import { ReactChildren } from '../../interfaces/Common';
import storage from '../../storageService';
import { Logout } from '../../Utils/apiService';

export const initialState: AuthState = {
  isAuthenticated: false,
  user: null,
  search: null,
};

const reducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case 'LOGIN':
      const userPayload = action.payload || { user: null };
      const user = userPayload.user || null;

      if (user) {
        localStorage.setItem('KEY_REFRESH_TOKEN', user.refreshToken?.token ?? '');
        localStorage.setItem('KEY_ACCESS_TOKEN', user.accessToken?.token ?? '');
        localStorage.setItem('user', JSON.stringify(user));
      }

      return {
        ...state,
        isAuthenticated: !!(user && Object.keys(user).length),
        user,
      };

    case 'LOGOUT':
      const existingUser = JSON.parse(localStorage.getItem('user') || '{}');
      if (existingUser?.applicantId) {
        Logout({
          contactId: existingUser.applicantId,
          portalLogout: true,
        });
      }
      storage.clear();
      localStorage.clear();
      return {
        ...state,
        isAuthenticated: false,
        user: null,
        search: null,
      };

    default:
      return state;
  }
};

export const AuthContext = createContext<{
  state: AuthState;
  dispatch: Dispatch<AuthAction>;
}>({ state: initialState, dispatch: () => {} });

export const AuthContextProvider: React.FunctionComponent<ReactChildren> = (props) => {
  const [state, dispatch] = useReducer(reducer, initialState);

  useEffect(() => {
    // Only fetch user if not already authenticated
    if (state.isAuthenticated) return;

    const fetchUserData = async () => {
      try {
        const storedUser = (await storage.get('user')) || localStorage.getItem('user');
        if (storedUser) {
          const parsedUser: AuthUser = JSON.parse(storedUser);
          dispatch({
            type: 'LOGIN',
            payload: { user: parsedUser },
          });
        }
      } catch (error) {
        console.error('Failed to fetch user data', error);
      }
    };

    fetchUserData();
  }, [state.isAuthenticated]);

  return <AuthContext.Provider value={{ state, dispatch }}>{props.children}</AuthContext.Provider>;
};
